import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  GitBranch, FileText, RefreshCw, ChevronRight, Maximize2, Minimize2,
  Download, FileDown,
} from "lucide-react";
import { currency } from "@/lib/constants";

const REL = {
  parent:    { color: "bg-slate-700",    text: "MSA",       Icon: GitBranch },
  amendment: { color: "bg-amber-600",    text: "AMEND",     Icon: FileText  },
  addendum:  { color: "bg-blue-600",     text: "ADDEND",    Icon: FileText  },
  renewal:   { color: "bg-emerald-600",  text: "RENEW",     Icon: RefreshCw },
  self:      { color: "bg-[#005289]",    text: "THIS",      Icon: FileText  },
  linked:    { color: "bg-slate-500",    text: "LINK",      Icon: GitBranch },
};

/** Horizontal date-sorted timeline of every contract in the family (parent +
 *  self + siblings). Compact = arc/dots. Detail = expanded cards with amount,
 *  stage, attorney and dates. Compact is the default.
 *  A "Export PDF" menu (audit / visual / both) hands off to the server. */
export default function FamilyTimeline({ contractId, contract }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("compact");       // compact | detail
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    if (!contractId) return;
    api.get(`/contracts/${contractId}/linked`).then(r => setData(r.data)).catch(() => setData(null));
  }, [contractId]);

  if (!data) return null;
  const anchor = data.parent || data.self || {
    id: contractId,
    contract_number: contract?.contract_number,
    vendor: contract?.vendor_legal_name,
    stage: contract?.stage,
  };
  const nodes = [];
  const selfNode = data.self || {
    id: contractId,
    contract_number: contract?.contract_number,
    vendor: contract?.vendor_legal_name,
    stage: contract?.stage,
    amount: contract?.contract_amount,
    attorney: contract?.assigned_attorney_name,
    executed_date: contract?.executed_date,
    initial_received_date: contract?.initial_received_date,
    expiration_date: contract?.expiration_date,
    created_at: contract?.created_at,
  };
  nodes.push({ ...anchor,
               relation: data.parent ? "parent" : "self",
               date: anchor.initial_received_date || anchor.created_at ||
                     contract?.initial_received_date });
  const isChild = !!data.parent;
  if (isChild) {
    nodes.push({ ...selfNode, relation: "self",
                 date: selfNode.initial_received_date || selfNode.created_at });
  }
  for (const c of (data.children || [])) {
    if (c.id === contractId) continue;
    nodes.push({ ...c, date: c.created_at });
  }
  if (nodes.length <= 1) return null;

  nodes.sort((a, b) => (a.date || "z").localeCompare(b.date || "z"));

  const exportPdf = async (style) => {
    setPdfBusy(true); setPdfOpen(false);
    try {
      const token = localStorage.getItem("token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/contracts/${contractId}/family-timeline.pdf?style=${style}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = `family-timeline-${contract?.contract_number || contractId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(dlUrl);
      toast.success("PDF downloaded");
    } catch (e) { toast.error(e.message || "PDF export failed"); }
    finally { setPdfBusy(false); }
  };

  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-md p-4" data-testid="family-timeline">
      <div className="flex items-center gap-2 mb-4">
        <GitBranch className="w-4 h-4 text-[#005289]" />
        <div className="text-sm font-bold text-slate-900">Family Timeline</div>
        <div className="text-[10px] text-slate-500">{nodes.length} contracts in this vendor relationship</div>
        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={() => setMode(mode === "compact" ? "detail" : "compact")}
            data-testid="timeline-zoom-toggle"
            title={mode === "compact" ? "Show details on every node" : "Collapse to compact arc"}
            className="h-7 px-2 text-[11px] font-semibold border border-slate-200 rounded hover:bg-slate-50 flex items-center gap-1">
            {mode === "compact"
              ? (<><Maximize2 className="w-3 h-3" /> Detail</>)
              : (<><Minimize2 className="w-3 h-3" /> Compact</>)}
          </button>
          <div className="relative">
            <button
              onClick={() => setPdfOpen(v => !v)}
              disabled={pdfBusy}
              data-testid="timeline-pdf-btn"
              className="h-7 px-2 text-[11px] font-semibold border border-slate-200 rounded hover:bg-slate-50 flex items-center gap-1 disabled:opacity-40">
              <FileDown className="w-3 h-3" /> {pdfBusy ? "Exporting…" : "Export PDF"}
            </button>
            {pdfOpen && (
              <div className="absolute right-0 mt-1 z-20 w-48 bg-white border border-slate-200 rounded shadow-md text-xs"
                   data-testid="timeline-pdf-menu">
                <button onClick={() => exportPdf("audit")}
                        data-testid="timeline-pdf-audit"
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2">
                  <Download className="w-3 h-3 text-slate-500" />
                  <div>
                    <div className="font-semibold">Audit-report style</div>
                    <div className="text-[10px] text-slate-500">Print-safe · regulatory</div>
                  </div>
                </button>
                <button onClick={() => exportPdf("visual")}
                        data-testid="timeline-pdf-visual"
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 border-t border-slate-100">
                  <Download className="w-3 h-3 text-slate-500" />
                  <div>
                    <div className="font-semibold">Visual timeline</div>
                    <div className="text-[10px] text-slate-500">Matches on-screen badges</div>
                  </div>
                </button>
                <button onClick={() => exportPdf("both")}
                        data-testid="timeline-pdf-both"
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 border-t border-slate-100">
                  <Download className="w-3 h-3 text-slate-500" />
                  <div>
                    <div className="font-semibold">Both</div>
                    <div className="text-[10px] text-slate-500">Audit + visual</div>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {mode === "compact" ? (
        <CompactRow nodes={nodes} contractId={contractId} nav={nav} />
      ) : (
        <DetailGrid nodes={nodes} contractId={contractId} nav={nav} />
      )}
    </div>
  );
}

function CompactRow({ nodes, contractId, nav }) {
  return (
    <div className="relative">
      <div className="absolute left-0 right-0 top-8 h-0.5 bg-slate-200" />
      <div className="flex justify-between items-start relative overflow-x-auto pb-2">
        {nodes.map((n, i) => {
          const s = REL[n.relation] || REL.linked;
          const isSelf = n.relation === "self" || n.id === contractId;
          return (
            <div key={n.id + i} className="flex flex-col items-center flex-1 min-w-[140px] px-1"
                 data-testid={`timeline-node-${n.contract_number}`}>
              <button onClick={() => n.id !== contractId && nav(`/contract/${n.id}`)}
                      className={`relative z-10 w-16 h-16 rounded-full flex flex-col items-center justify-center text-white shadow-md ${s.color} ${isSelf ? "ring-4 ring-[#005289]/30 scale-110" : "hover:scale-105"} transition`}
                      title={`#${n.contract_number} · ${n.vendor} · ${(n.stage||"").replace(/_/g,' ')}`}>
                <s.Icon className="w-4 h-4 mb-0.5" />
                <div className="text-[8px] font-bold tracking-wider">{s.text}</div>
              </button>
              <div className="mt-2 text-center min-w-0 max-w-[140px]">
                <div className="text-[10px] font-mono text-slate-500 truncate">#{n.contract_number}</div>
                <div className="text-[10px] text-slate-500 truncate">{(n.stage || "").replace(/_/g,' ')}</div>
                {n.date && <div className="text-[9px] text-slate-400 mt-0.5">{new Date(n.date).toLocaleDateString()}</div>}
              </div>
              {i < nodes.length - 1 && (
                <ChevronRight className="absolute right-[-10px] top-6 w-4 h-4 text-slate-300 z-10 hidden md:block" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DetailGrid({ nodes, contractId, nav }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="timeline-detail-grid">
      {nodes.map((n, i) => {
        const s = REL[n.relation] || REL.linked;
        const isSelf = n.relation === "self" || n.id === contractId;
        return (
          <button
            key={n.id + i}
            onClick={() => n.id !== contractId && nav(`/contract/${n.id}`)}
            data-testid={`timeline-detail-${n.contract_number}`}
            className={`text-left border rounded-md p-3 transition hover:shadow-sm ${isSelf ? "border-[#005289] ring-1 ring-[#005289]/30 bg-slate-50" : "border-slate-200 hover:bg-slate-50"}`}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-white text-[9px] font-bold tracking-wider ${s.color}`}>
                <s.Icon className="w-3 h-3" /> {s.text}
              </span>
              <span className="text-[10px] font-mono text-slate-500">#{n.contract_number}</span>
              {isSelf && <span className="text-[9px] px-1 py-0.5 rounded bg-[#005289] text-white font-bold ml-auto">CURRENT</span>}
            </div>
            <div className="text-xs font-semibold text-slate-900 truncate mb-1">{n.vendor}</div>
            <div className="text-[10px] text-slate-500 capitalize mb-2">
              {(n.stage || "unknown").replace(/_/g, " ")}
              {n.contract_type ? <span className="ml-2 text-slate-400">· {n.contract_type}</span> : null}
            </div>
            <div className="grid grid-cols-2 gap-1 text-[10px]">
              <div>
                <div className="text-slate-400">Amount</div>
                <div className="font-semibold text-slate-800">{n.amount ? currency(n.amount) : "—"}</div>
              </div>
              <div>
                <div className="text-slate-400">Attorney</div>
                <div className="font-semibold text-slate-800 truncate">{n.attorney || "—"}</div>
              </div>
              <div>
                <div className="text-slate-400">Received</div>
                <div className="text-slate-700">{n.initial_received_date ? new Date(n.initial_received_date).toLocaleDateString() : (n.created_at ? new Date(n.created_at).toLocaleDateString() : "—")}</div>
              </div>
              <div>
                <div className="text-slate-400">Executed</div>
                <div className="text-slate-700">{n.executed_date ? new Date(n.executed_date).toLocaleDateString() : "—"}</div>
              </div>
              {n.expiration_date && (
                <div className="col-span-2">
                  <div className="text-slate-400">Expires</div>
                  <div className="text-slate-700">{new Date(n.expiration_date).toLocaleDateString()}</div>
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
